
import React, { useMemo, useState } from 'react';
import { 
  TrendingUp, 
  Calendar, 
  Clock, 
  ArrowRight,
  Plus,
  Users,
  MapPin,
  AlertCircle,
  Activity,
  DollarSign,
  Target,
  Edit2,
  Check,
  CalendarDays
} from 'lucide-react';
import { Inspection, Contact, Invoice, Location } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

interface DashboardProps {
  inspections: Inspection[];
  contacts: Contact[];
  invoices: Invoice[];
  locations: Location[];
  onNavigateToInspection: (id: string) => void;
}

type TabType = 'active' | 'upcoming' | 'completed';

const Dashboard: React.FC<DashboardProps> = ({ inspections, contacts, invoices, locations, onNavigateToInspection }) => {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [inspectionGoal, setInspectionGoal] = useState(150);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [tempGoal, setTempGoal] = useState(inspectionGoal.toString());

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const todayStr = now.toISOString().split('T')[0];
  const currentYear = now.getFullYear();

  const metrics = useMemo(() => {
    const ytdInvoices = invoices.filter(inv => {
      const invDate = new Date(inv.createdAt);
      return invDate.getFullYear() === currentYear && inv.status === 'paid';
    });
    const ytdInspections = inspections.filter(ins => {
      const insDate = new Date(ins.date);
      return insDate.getFullYear() === currentYear;
    });
    const revenueYTD = ytdInvoices.reduce((acc, inv) => acc + (inv.total || 0), 0);
    const bookedYTD = ytdInspections.length;
    const goalProgress = Math.min(100, Math.round((bookedYTD / inspectionGoal) * 100));
    return { revenueYTD, bookedYTD, goalProgress };
  }, [invoices, inspections, currentYear, inspectionGoal]);

  const filteredInspections = useMemo(() => {
    switch (activeTab) {
      case 'active': return inspections.filter(i => i.date === todayStr);
      case 'upcoming': return inspections.filter(i => i.date > todayStr && i.status !== 'Cancelled').sort((a, b) => a.date.localeCompare(b.date));
      case 'completed': return inspections.filter(i => i.status === 'Completed').sort((a, b) => b.date.localeCompare(a.date));
      default: return [];
    }
  }, [inspections, activeTab, todayStr]);

  const getPropertyAddress = (id: string) => locations.find(l => l.id === id)?.address || 'Unknown Asset';
  const getContactName = (id: string) => contacts.find(c => c.id === id)?.name || 'Unknown Contact';

  const getDaysUntil = (dateStr: string) => {
    const inspectionDate = new Date(dateStr);
    inspectionDate.setHours(0, 0, 0, 0);
    const diffTime = inspectionDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return <span className="text-emerald-500 font-black">Today</span>;
    if (diffDays === 1) return <span className="text-blue-500 font-bold">Tomorrow</span>;
    if (diffDays < 0) return <span className="text-slate-400 font-medium">{Math.abs(diffDays)}d ago</span>;
    return <span className="text-slate-600 font-bold">In {diffDays}d</span>;
  };

  const handleGoalSave = () => {
    const val = parseInt(tempGoal);
    if (!isNaN(val) && val > 0) setInspectionGoal(val);
    setIsEditingGoal(false);
  };

  const gaugeData = [{ value: metrics.bookedYTD }, { value: Math.max(0, inspectionGoal - metrics.bookedYTD) }];

  return (
    <div className="space-y-4 animate-in fade-in duration-500 pb-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main List Section */}
        <div className="lg:col-span-2 flex flex-col space-y-4 order-2 lg:order-1">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[500px] lg:h-[750px] overflow-hidden">
            <div className="px-4 py-4 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between bg-slate-50/30 gap-4">
              <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl w-full sm:w-auto">
                {(['active', 'upcoming', 'completed'] as TabType[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                      activeTab === tab ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:block">
                {filteredInspections.length} Records found
              </span>
            </div>

            <div className="flex-1 overflow-x-auto no-scrollbar">
              {filteredInspections.length > 0 ? (
                <table className="w-full text-left border-collapse min-w-max">
                  <thead className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-10">
                    <tr>
                      <th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Asset Address</th>
                      <th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Stakeholder</th>
                      <th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">Schedule</th>
                      <th className="px-6 py-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInspections.map((ins) => (
                      <tr 
                        key={ins.id} 
                        onClick={() => onNavigateToInspection(ins.id)}
                        className="hover:bg-slate-50/50 transition-colors group cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 group-hover:text-blue-500 group-hover:bg-white transition-all shadow-sm">
                              <MapPin size={16} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-800 truncate">{getPropertyAddress(ins.propertyId)}</p>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{ins.status}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Users size={14} className="text-slate-300" />
                            <span className="text-xs font-semibold text-slate-600">{getContactName(ins.contactId)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5 text-slate-500">
                              <Calendar size={12} className="text-slate-300" />
                              <span className="text-[10px] font-bold">{ins.date}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-400">
                              <Clock size={12} className="text-slate-300" />
                              <span className="text-[10px] font-medium">{ins.time}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <span className="text-[10px] uppercase font-bold tracking-tight">{getDaysUntil(ins.date)}</span>
                            <ArrowRight size={16} className="text-slate-200 group-hover:text-blue-500 transition-all" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-300 space-y-3 p-10 text-center">
                  <AlertCircle size={40} strokeWidth={1.5} className="text-slate-200" />
                  <p className="text-[10px] font-black uppercase tracking-[0.2em]">Zero Records Found</p>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-slate-50 border-t border-slate-100">
              <button className="w-full py-3 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center gap-2 shadow-sm">
                <Plus size={16} /> Schedule New Field Session
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="space-y-6 order-1 lg:order-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
              <div className="absolute right-0 bottom-0 p-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity"><TrendingUp size={80} /></div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1"><DollarSign size={10} /> Revenue YTD</p>
              <h3 className="text-3xl font-black text-slate-800 tracking-tighter">${metrics.revenueYTD.toLocaleString()}</h3>
              <div className="h-1.5 w-full bg-slate-100 rounded-full mt-4 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: '65%' }}></div></div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
              <div className="absolute right-0 bottom-0 p-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity"><CalendarDays size={80} /></div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1"><Activity size={10} /> Sessions Booked</p>
              <h3 className="text-3xl font-black text-slate-800 tracking-tighter">{metrics.bookedYTD}</h3>
              <p className="text-[9px] font-bold text-blue-500 uppercase tracking-widest mt-1.5">Annual Volume Indexed</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center">
            <div className="w-full flex justify-between items-center mb-6">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Target size={14} className="text-blue-500" /> Efficiency Goal</h4>
              {isEditingGoal ? (
                <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200">
                  <input type="number" className="w-14 bg-transparent text-xs font-bold outline-none text-center" value={tempGoal} onChange={(e) => setTempGoal(e.target.value)} autoFocus />
                  <button onClick={handleGoalSave} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"><Check size={14} /></button>
                </div>
              ) : (
                <button onClick={() => { setTempGoal(inspectionGoal.toString()); setIsEditingGoal(true); }} className="p-2 text-slate-300 hover:text-blue-600 hover:bg-slate-50 rounded-lg"><Edit2 size={12} /></button>
              )}
            </div>

            <div className="relative w-full h-48 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={gaugeData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={60} outerRadius={90} paddingAngle={0} dataKey="value" stroke="none" animationDuration={1000}>
                    <Cell fill="#2563eb" /><Cell fill="#f1f5f9" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-x-0 bottom-4 flex flex-col items-center">
                <span className="text-4xl font-black text-slate-900 tracking-tighter">{metrics.goalProgress}%</span>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Yearly Capacity</span>
              </div>
            </div>

            <div className="w-full space-y-4">
              <div className="text-center bg-slate-50 py-4 rounded-2xl border border-slate-100">
                <p className="text-xl font-black text-slate-800">{metrics.bookedYTD} <span className="text-slate-300 mx-1">/</span> {inspectionGoal}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Inspections Planned</p>
              </div>
              <div className="p-5 bg-slate-900 rounded-2xl relative shadow-lg overflow-hidden group">
                 <div className="absolute top-0 right-0 p-3 opacity-10 text-white group-hover:scale-110 transition-transform"><Activity size={40} /></div>
                 <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1.5">Audit Intelligence</p>
                 <p className="text-xs text-slate-400 font-medium leading-relaxed">You have indexed {metrics.goalProgress}% of your target. {metrics.goalProgress < 50 ? 'Maintain velocity.' : 'Ahead of trajectory.'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
